import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AccountingModeProvider } from './contexts/AccountingModeContext';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/DashboardLayout';
import Dashboard from './pages/Dashboard';
import ConfigurationPage from './pages/ConfigurationPage';
import CalculatorPage from './pages/CalculatorPage';
import ProductionPage from './pages/ProductionPage';
import ClientsPage from './pages/ClientsPage';
import ProductsServicesPage from './pages/ProductsServicesPage';
import SalesPage from './pages/SalesPage';
import PurchasesPage from './pages/PurchasesPage';
import ExpensesPage from './pages/ExpensesPage';
import RHPage from './pages/RHPage';
import AnalyticsPage from './pages/AnalyticsPage';
import StockPage from './pages/StockPage';
import RebusPage from './pages/RebusPage';
import AggloSalesPage from './pages/AggloSalesPage';
import GranulatsPage from './pages/GranulatsPage';
import ValidationPage from './pages/ValidationPage';
import UsersPage from './pages/UsersPage';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PermissionRoute = ({ children, permission }) => {
  const { user, loading, hasPermission } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (permission && !hasPermission(permission)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;
  }
  
  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <AccountingModeProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Public validation route - no auth required */}
            <Route path="/validation/:token" element={<ValidationPage />} />
            <Route path="/" element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }>
              <Route index element={<Dashboard />} />
              <Route path="configuration" element={
                <PermissionRoute permission="can_manage_config">
                  <ConfigurationPage />
                </PermissionRoute>
              } />
              <Route path="calculator" element={<CalculatorPage />} />
              <Route path="production" element={<ProductionPage />} />
              <Route path="clients" element={<ClientsPage />} />
              <Route path="products-services" element={<ProductsServicesPage />} />
              <Route path="sales" element={<SalesPage />} />
              <Route path="purchases" element={<PurchasesPage />} />
              <Route path="expenses" element={
                <PermissionRoute permission="can_manage_expenses">
                  <ExpensesPage />
                </PermissionRoute>
              } />
              <Route path="rh" element={
                <PermissionRoute permission="can_manage_hr">
                  <RHPage />
                </PermissionRoute>
              } />
              <Route path="analytics" element={
                <PermissionRoute permission="can_view_analytics">
                  <AnalyticsPage />
                </PermissionRoute>
              } />
              <Route path="stock" element={<StockPage />} />
              <Route path="rebus" element={<RebusPage />} />
              <Route path="granulats" element={<GranulatsPage />} />
              <Route path="agglo-sales" element={<AggloSalesPage />} />
              <Route path="users" element={
                <PermissionRoute permission="can_manage_users">
                  <UsersPage />
                </PermissionRoute>
              } />
            </Route>
          </Routes>
        </BrowserRouter>
      </AccountingModeProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
